import {
  trackEvent as trackGAEvent,
  trackPageView as trackGAPageView,
} from './GoogleAnalyticsTrackEvents';
import {
  trackClarityEvent,
  trackClarityPageView,
  trackUserInteraction as trackClarityUserInteraction,
} from './ClarityTrackEvents';
import { capturePostHogEvent } from '@/config/posthog';

/**
 * Combined analytics tracker for Google Analytics and Clarity
 */
export class AnalyticsTracker {
  /**
   * Track a custom event with GA and Clarity
   * @param {string} eventName - The name of the event
   * @param {object} eventParams - Event parameters
   */
  static trackEvent(eventName, eventParams = {}) {
    // Only track in production
    if (import.meta.env.VITE_APP_ENV === 'production') {
      trackGAEvent(eventName, eventParams);
      trackClarityEvent(eventName, eventParams);
      // Mirror to PostHog so the existing 40+ call sites (auth_success,
      // tournament_search, payment events, etc.) light up product-analytics
      // dashboards without per-component changes.
      capturePostHogEvent(eventName, eventParams);
    } else {
      // Enhanced logging for development
      console.log(
        '%c🔍 Analytics Event (DEV)',
        'background: #FF9800; color: white; padding: 2px 5px; border-radius: 3px;',
        '\nEvent:',
        eventName,
        '\nParams:',
        eventParams,
      );
    }
  }

  /**
   * Track page views with all platforms
   * @param {string} path - The page path
   * @param {string} title - The page title
   */
  static trackPageView(path, title) {
    // Only track in production
    if (import.meta.env.VITE_APP_ENV === 'production') {
      trackGAPageView(path, title);
      trackClarityPageView(path, title);
      // PostHog pageviews are intentionally NOT fired here — the React
      // Router location listener in App.tsx is the single source of truth
      // for $pageview, so duplicate calls here would double-count.
    } else {
      console.log('Page View (DEV):', { path, title });
    }
  }

  /**
   * Track user interactions (clicks, form submissions, etc.)
   * @param {string} action - The action being performed
   * @param {string} element - The element being interacted with
   * @param {object} additionalData - Additional data
   */
  static trackUserInteraction(action, element, additionalData = {}) {
    const eventData = {
      action,
      element,
      ...additionalData,
      timestamp: new Date().toISOString(),
    };

    AnalyticsTracker.trackEvent('user_interaction', eventData);
  }

  /**
   * Track button clicks
   * @param {string} buttonName - Name/ID of the button
   * @param {string} page - Current page
   * @param {object} additionalData - Additional data
   */
  static trackButtonClick(buttonName, page, additionalData = {}) {
    AnalyticsTracker.trackUserInteraction('click', buttonName, {
      page,
      element_type: 'button',
      ...additionalData,
    });
  }

  /**
   * Track form submissions
   * @param {string} formName - Name/ID of the form
   * @param {string} page - Current page
   * @param {object} additionalData - Additional data
   */
  static trackFormSubmission(formName, page, additionalData = {}) {
    AnalyticsTracker.trackUserInteraction('submit', formName, {
      page,
      element_type: 'form',
      ...additionalData,
    });
  }

  /**
   * Track navigation events
   * @param {string} fromPage - Source page
   * @param {string} toPage - Destination page
   * @param {string} navigationType - Type of navigation (link, button, etc.)
   */
  static trackNavigation(fromPage, toPage, navigationType = 'link') {
    AnalyticsTracker.trackEvent('navigation', {
      from_page: fromPage,
      to_page: toPage,
      navigation_type: navigationType,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track feature usage
   * @param {string} featureName - Name of the feature
   * @param {string} action - Action performed on the feature
   * @param {object} additionalData - Additional data
   */
  static trackFeatureUsage(featureName, action, additionalData = {}) {
    AnalyticsTracker.trackEvent('feature_usage', {
      feature: featureName,
      action,
      ...additionalData,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track errors
   * @param {string} errorType - Type of error
   * @param {string} errorMessage - Error message
   * @param {string} page - Page where error occurred
   */
  static trackError(errorType, errorMessage, page) {
    AnalyticsTracker.trackEvent('error', {
      error_type: errorType,
      error_message: errorMessage,
      page,
      timestamp: new Date().toISOString(),
    });
  }
}

// Export individual functions for convenience
export const {
  trackEvent,
  trackPageView,
  trackUserInteraction,
  trackButtonClick,
  trackFormSubmission,
  trackNavigation,
  trackFeatureUsage,
  trackError,
} = AnalyticsTracker;
